import React from "react";
import Image from "next/image";
import styles from "../../styles/shap_of_you.module.scss";
import SharePage from "../../assets/images/SharePage.png";
import logoleft from "../../assets/images/logo-left.png";

import { ShareViewScreenProps } from "./types";
import { toast } from "react-toastify";

const ShareViewScreen: React.FC<ShareViewScreenProps> = ({
  navigateToStep,
  virtualTryOnImage,
  capturedImage,
  logStep,
}) => {
  // Determine which image to display (same priority as mobile)
  // Priority: virtualTryOnImage (with jewelry) > capturedImage (original) > default
  const displayImage = virtualTryOnImage || capturedImage || SharePage;
  const isBase64 =
    typeof displayImage === "string" && displayImage.startsWith("data:");

  // Composite the user photo with both logos using Canvas
  const compositeImageWithLogos = (imageUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas not supported"));

      const mainImg = new window.Image();
      mainImg.crossOrigin = "anonymous";
      mainImg.onload = () => {
        canvas.width = mainImg.width;
        canvas.height = mainImg.height;
        ctx.drawImage(mainImg, 0, 0);

        const padding = canvas.width * 0.03;
        const logoHeight = canvas.height * 0.08;

        const shapeImg = new window.Image();
        shapeImg.crossOrigin = "anonymous";
        shapeImg.onload = () => {
          // Draw Shape of You logo — bottom-left
          const shapeW = (shapeImg.width / shapeImg.height) * logoHeight;
          ctx.drawImage(
            shapeImg,
            padding,
            canvas.height - logoHeight - padding,
            shapeW,
            logoHeight
          );

          // Draw text — bottom-right
          const fontSize = Math.max(12, canvas.height * 0.022);
          ctx.font = `${fontSize}px sans-serif`;
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "right";
          ctx.shadowColor = "rgba(0,0,0,0.6)";
          ctx.shadowBlur = 4;
          const line1 = "Shop jewellery made for your face shape";
          const line2 = "www.sencogoldanddiamonds.com";
          const lineSpacing = fontSize * 1.4;
          const textX = canvas.width - padding;
          const textY = canvas.height - padding - lineSpacing;
          ctx.fillText(line1, textX, textY);
          ctx.fillText(line2, textX, textY + lineSpacing);
          ctx.shadowBlur = 0;
          resolve(canvas.toDataURL("image/jpeg", 0.92));
        };
        shapeImg.onerror = () => resolve(canvas.toDataURL("image/jpeg", 0.92));
        shapeImg.src = (logoleft as any).src ?? (logoleft as any);
      };
      mainImg.onerror = reject;
      mainImg.src = imageUrl;
    });
  };

  // Handle download functionality
  const handleDownload = async () => {
    try {
      const downloadImage = virtualTryOnImage || capturedImage;
      if (!downloadImage) {
        toast.error("No image available to download");
        return;
      }

      const composited = await compositeImageWithLogos(downloadImage);

      const link = document.createElement("a");
      link.href = composited;
      link.download = "senco-jewelry-tryon.jpg";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);

      if (logStep) logStep("share_view_download_clicked");
      toast.success("Image downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading:", error);
      toast.error("Failed to download image. Please try again.");
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    try {
      if (!isBase64) {
        toast.error("No image available to share");
        return;
      }

      const composited = await compositeImageWithLogos(displayImage as string);

      // Convert composited base64 to blob
      const base64Response = await fetch(composited);
      const blob = await base64Response.blob();
      const file = new File([blob], "senco-jewelry-tryon.jpg", {
        type: "image/jpeg",
      });

      if (
        "share" in navigator &&
        "canShare" in navigator &&
        (navigator as any).canShare({ files: [file] })
      ) {
        await navigator.share({
          title: "My Senco Jewellery Look",
          text: "Check out how I look with Senco Jewellery! They suggested pieces based on my face shape —https://sencogoldanddiamonds.com/shape-of-you you should try this too!",
          files: [file],
        });
      } else if ("share" in navigator) {
        await navigator.share({
          title: "My Senco Jewellery Look",
          text: "Check out how I look with Senco Jewellery! They suggested pieces based on my face shape —https://sencogoldanddiamonds.com/shape-of-you you should try this too!",
          url: window.location.href,
        });
      } else {
        const link = document.createElement("a");
        link.href = composited;
        link.download = "senco-jewelry-tryon.jpg";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Image downloaded successfully");
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("Error sharing:", error);
        toast.error("Failed to share. Please try again.");
      }
    }
  };

  return (
    <div className="d-none d-md-block w-100">
      <div className={styles.SharePage_desktop}>
        <div className={styles.bgImage}>
          <div className={styles.image_center}>
            {virtualTryOnImage ? (
              <img src={virtualTryOnImage} alt="Virtual Try On" />
            ) : capturedImage ? (
              <img src={capturedImage} alt="Captured Face" />
            ) : (
              <Image src={SharePage} alt="Share Page" />
            )}
          </div>
        </div>
        <div className={styles.shapeOfYou}>
          <button
            className={styles.dwn}
            onClick={handleDownload}
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-download"></i>
          </button>
          <button
            className={styles.back_arrow}
            onClick={() => {
              if (logStep) {
                logStep("share_view_back_clicked");
              }
              navigateToStep("productView");
            }}
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <div className={styles.bottom_container}>
            <div className={styles.logo_left}>
              <Image src={logoleft} alt="" />
            </div>
            <div className={styles.text_right}>
              <p>
                Shop jewellery made for your face shape <br />
                <a href="https://sencogoldanddiamonds.com/">
                  www.sencogoldanddiamonds.com
                </a>
              </p>
            </div>
          </div>
          <div className={styles.container_btn}>
            <button
              className={styles.editNumber}
              onClick={() => {
                if (logStep) {
                  logStep("share_view_retake_clicked");
                }
                navigateToStep("uploadOrScan");
              }}
            >
              <svg
                className="mx-1"
                width="18"
                height="18"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.19386 6.64893C10.8233 4.37532 14.251 3 18 3C26.2842 3 33 9.71572 33 18C33 21.2041 31.9953 24.1737 30.2838 26.6109L25.5 18H30C30 11.3726 24.6275 6 18 6C14.7747 6 11.8467 7.2724 9.69034 9.34263L8.19386 6.64893ZM27.8061 29.3511C25.1766 31.6246 21.749 33 18 33C9.71572 33 3 26.2842 3 18C3 14.7958 4.00469 11.8262 5.7162 9.38916L10.5 18H6C6 24.6275 11.3726 30 18 30C21.2253 30 24.1533 28.7276 26.3097 26.6574L27.8061 29.3511Z"
                  fill="white"
                />
              </svg>
              Retake
            </button>
            <button className={styles.submitBtn1} onClick={handleShare}>
              <svg
                className="mx-1"
                width="18"
                height="17"
                viewBox="0 0 20 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.0002 1V5C4.42519 6.028 1.98019 11.788 1.00019 17C0.963188 17.206 6.38419 11.038 11.0002 11V15L19.0002 8L11.0002 1Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareViewScreen;
